/****************************************************************************
 **
 ** Copyright (C) 2021 The Qt Company Ltd.
 ** Contact: https://www.qt.io/licensing/
 **
 ** This file is part of Qt Bridge Plugin.
 **
 ** Commercial License Usage
 ** Licensees holding valid commercial Qt licenses may use this file in
 ** accordance with the commercial license agreement provided with the
 ** Software or, alternatively, in accordance with the terms contained in
 ** a written agreement between you and The Qt Company. For licensing terms
 ** and conditions see https://www.qt.io/terms-conditions. For further
 ** information use the contact form at https://www.qt.io/contact-us.
 **
 ****************************************************************************/

import Path from "path"
import sketchDom from "sketch/dom";

import { Document, Library } from "sketch/dom";
import { SDocument } from "./sdocument.js";
import { SLayer } from "./slayer.js";
import { BUtils, ViewApi } from "./qtbridgeutils.js";
import {
    SketchLayerType as SLT,
    ExportType as ET,
    ValidExportFormats as VEF
} from "./constants.js";
import * as Constants from "./constants.js";
import { SStyle } from "./sstyle.js";
import JSZip from 'jszip';
import * as FS from "@skpm/fs";

const OverrideQmlMapping = new Map([
    ["stringvalue", textToQml],
    ["symbolid", symbolIdToUuid],
    ["textstyle", styleToQml]
]);

const ScaleProps = new Set(["x", "y", "width", "height", "fontSize", "lineHeight", "kerning"]);

export class Export {
    constructor(documentInstance) {
        if (!documentInstance || documentInstance.type != "Document") {
            BUtils.logExportError("Invalid Document instance.");
            return;
        }
        this._sDoc = new SDocument(documentInstance);
    }

    async doExport() {
        try {
            if (!this._sDoc) {
                BUtils.logExportError("Invalid Document instance.");
                return false;
            }

            const exportDir = this._sDoc.exportPath();
            if (!BUtils.makePath(exportDir)) {
                BUtils.logExportError(`Cannot create the export archive path. ${archiveFilePath}`);
                return false
            }

            const archiveName = `${BUtils.strToFileName(this._sDoc.documentName())}.qtbridge`;
            const archiveFilePath = Path.join(exportDir, archiveName);
            if (BUtils.fileExists(archiveFilePath)) {
                let continueExport = BUtils.showAssertion(
                    "?",
                    `${archiveName} already exists. File shall be overwriten. Continue?`
                );
                if (!continueExport) {
                    BUtils.logExportInfo("Export aborted.");
                    return false;
                }
            }

            let aslocal = false;
            if (this._sDoc.haveRemoteSymbols()) {
                aslocal = BUtils.showAssertion(
                    "Export library?",
                    `Document uses library symbols.\nDo you want to export complete library or export remote symbols as local?`,
                    "As local",
                    "With libraries"
                );
            }

            let archive = new JSZip();
            let libraryDependencies = new Map();
            if (aslocal) {
                unlinkLibraryInstances.call(this);
            } else {
                libraryDependencies = this.exportLinkedLibraries(archive);
            }
            let auxData = { symbolMasterReference: new Map() };
            let libPaths = compileLibPaths(this._sDoc, libraryDependencies);
            await this.exportToArchive(archive, auxData, libPaths);
            if (aslocal) {
                relinkLibraryInstances.call(this)
            }

            // Write the archive.
            BUtils.logExportInfo(`Writing archive at ${archiveFilePath}`);
            const content = await archive.generateAsync({
                type: "uint8array"
            });
            FS.writeFileSync(archiveFilePath, content);
            return true;
        } catch (e) {
            BUtils.logExportError(`Exporting failed. ${e}`);
        }
        return false;
    }

    exportLinkedLibraries(archive) {
        let linkedLibs = new Map();
        let libraryDependencies = new Map();
        findLinkedLibraries(this._sDoc, linkedLibs, libraryDependencies);

        if (linkedLibs.size > 0) {
            BUtils.logExportInfo(`\nExporting linked user libraries.`);
        }

        // Export linked libraries first.
        for (let [path, libInstance] of linkedLibs) {
            let libExport = new Export(libInstance.getDocument());
            let libExportDir = libraryExportPath(libInstance);
            let libAuxData = {
                symbolMasterReference: composeSymbolMasterReference(libInstance, this._sDoc)
            }
            let libLibPaths = compileLibPaths(libExport._sDoc, libraryDependencies);
            let archiveObj = archive.folder(libExportDir);
            libExport.exportToArchive(archiveObj, libAuxData, libLibPaths)
        }
        return libraryDependencies;
    }

    async exportToArchive(archiveObj, auxData, dependentLibPaths) {
        BUtils.logExportInfo(`\nExporting Document: ${this._sDoc.documentName()}`);
        let assetData = composeAssetData(this._sDoc);

        auxData.assetPaths = exportAssets(assetData, archiveObj, this._sDoc);
        //convertToRelativePaths(subDir, auxData.assetPaths);
        auxData.overrides = composeOverridesData(this._sDoc);
        let data = {
            pluginInfo: pluginInfo(),
            documentInfo: this._sDoc.documentInfo(this),
            artboards: artboardsData(this._sDoc, auxData),
            dependencies: dependentLibPaths
        };

        // Global scaling
        const sf = this._sDoc.uiScaleFactor();
        if (Math.abs(sf - 1.0) > Number.EPSILON)
            applyUiScale(data, sf);

        const metadataFileName = `${BUtils.strToFileName(this._sDoc.documentName())}.metadata`;
        BUtils.logExportInfo(`Writing Metadata to ${metadataFileName}`);
        archiveObj.file(metadataFileName, JSON.stringify(data, " ", 4));
    }

    static async exportActiveDocument() {
        BUtils.updateExportProgress(0);
        let e = new Export(Document.getSelectedDocument());
        let success = await e.doExport();
        if (success) {
            BUtils.logExportInfo(`Export done.`);
        } else {
            BUtils.logExportError(`Export failed.`);
        }
        BUtils.updateExportProgress(100);
        ViewApi("onExportFinished", success);
    }
}

function convertToRelativePaths(exportDir, assetPathSet) {
    for (let [key, pathData] of assetPathSet.entries()) {
        pathData.path = Path.relative(exportDir, pathData.path)
        if (pathData.additionalAssets) {
            pathData.additionalAssets.forEach((aPath, index) => {
                pathData.additionalAssets[index] = Path.relative(exportDir, aPath);
            });
        }
    }
}

function pluginInfo() {
    return {
        applicationId: "SketechApp",
        applicationVersion: BUtils.appVersion(),
        extensionVersion: BUtils.version(),
        metadataVersion: BUtils.metadataVersion()
    };
}

function artboardsData(sDoc, auxData) {
    BUtils.updateExportProgress(Constants.PSMetadata.start);
    BUtils.logExportInfo("Composing metadata");
    let progressShare = Constants.PSMetadata.end - Constants.PSMetadata.start;
    let artboards = [];
    sDoc.forEachArtboard(
        (artboardLayer, index) => {
            artboards.push(groupJson(artboardLayer, index, auxData));
        },
        done => {
            BUtils.updateExportProgress(Constants.PSMetadata.start + done * progressShare);
        }
    );
    BUtils.updateExportProgress(Constants.PSMetadata.end);
    return artboards;
}

function groupJson(sLayer, index, auxData) {
    if (!sLayer.instance()) {
        BUtils.logExportError("Invalid Layer instance.");
        return {};
    }

    let groupData = compileLayerData(sLayer, index, auxData);
    if (sLayer.haveChildren()) {
        groupData.children = [];
        sLayer.forEachChildLayer((childLayer, childIndex) => {
            if (childLayer.haveChildren()) {
                groupData.children.push(groupJson(childLayer, childIndex, auxData));
            } else {
                groupData.children.push(compileLayerData(childLayer, childIndex, auxData));
            }
        });
    }
    return groupData;
}

function compileLayerData(sLayer, layerIndex, auxData) {
    let jsonData = sLayer.toJson();
    jsonData.layerIndex = layerIndex;
    let lid = sLayer.layerId();
    if (auxData.assetPaths && auxData.assetPaths.has(lid)) {
        let pathData = auxData.assetPaths.get(lid);
        let assetData = {
            assetPath: pathData.path,
            additionalAssets: pathData.additionalAssets
        };
        assetData["assetBounds"] = sLayer.assetRect();
        jsonData.metadata["assetData"] = assetData;
    }
    if (auxData.overrides.aliases.has(lid)) {
        jsonData.metadata.aliases = [...auxData.overrides.aliases.get(lid)];
    }

    if (auxData.symbolMasterReference.has(lid)) {
        jsonData.metadata.uuid = auxData.symbolMasterReference.get(lid);
    }

    if (auxData.overrides.aliasOverrides.has(lid)) {
        jsonData.metadata.aliasOverrides = auxData.overrides.aliasOverrides.get(lid);
    }
    return jsonData;
}

function composeAssetData(sDoc) {
    let assetData = new Map();
    BUtils.updateExportProgress(Constants.PSCompose.start);
    BUtils.logExportInfo("Composing Assets");
    let progressShare = Constants.PSCompose.end - Constants.PSCompose.start;
    sDoc.forEachArtboard(addAsset.bind(null, assetData), done => {
        BUtils.updateExportProgress(Constants.PSCompose.start + done * progressShare);
    });
    BUtils.updateExportProgress(Constants.PSCompose.end);
    return assetData;
}

function addAsset(assetData, sLayer) {
    if (!sLayer.instance()) {
        BUtils.logExportError("Invalid Layer instance.");
        return {};
    }

    let data = {};
    data.exclude = [];
    if (sLayer.exportType() === ET.SkipExport || sLayer.layerType() == SLT.SymbolInstance) {
        // No assets shall be generated for skipped and symbol instances.
        return;
    }
    sLayer.forEachChildLayer(childLayer => {
        let cExportType = childLayer.exportType();
        if (cExportType === ET.AsChild) {
            data.exclude.push(childLayer.instance());
            if (childLayer.haveChildren()) {
                addAsset(assetData, childLayer);
            } else if (childLayer.canHaveAsset()) {
                assetData.set(childLayer.instance(), { exclude: [] });
            }
        } else if (cExportType === ET.SkipExport) {
            data.exclude.push(childLayer.instance());
        }
    });

    if (sLayer.haveAsset()) {
        assetData.set(sLayer.instance(), data);
    } else {
        BUtils.logExportWarning(`Skipping empty asset. Layer: ${sLayer.layerName()}`);
    }
}

function composeOverridesData(sDoc) {
    let overrideData = {
        aliases: new Map(),
        aliasOverrides: new Map()
    };
    BUtils.updateExportProgress(Constants.PSOverride.start);
    BUtils.logExportInfo("Composing override data");
    let progressShare = Constants.PSOverride.end - Constants.PSOverride.start;
    sDoc.forEachArtboard(addOverrides.bind(null, sDoc, overrideData), done => {
        BUtils.updateExportProgress(Constants.PSOverride.start + done * progressShare);
    });
    BUtils.updateExportProgress(Constants.PSOverride.end);
    return overrideData;
}

function addOverrides(sDoc, overrideData, sLayer) {
    if (!sLayer.instance()) {
        BUtils.logExportError("Invalid Layer instance.");
        return {};
    }

    if (sLayer.layerType() === SLT.SymbolInstance) {
        // Compose override data for this layer
        let overrideList = [];
        if (sLayer.instance().overrides) {
            sLayer.instance().overrides.forEach(override => {
                if (!override.isDefault) {
                    let properties = overrideToQmlProperties(override, sDoc, sLayer);
                    if (!properties) {
                        return;
                    }
                    properties.forEach(p => {
                        if (!overrideData.aliases.has(p.targetId)) {
                            overrideData.aliases.set(p.targetId, new Set());
                        }
                        overrideData.aliases.get(p.targetId).add(p.name);
                        overrideList.push(p);
                    });
                }
            });
        }
        if (overrideList.length > 0) {
            overrideData.aliasOverrides.set(sLayer.layerId(), overrideList);
        }
    }
    // Iterate child groups if any
    sLayer.forEachChildLayer(addOverrides.bind(null, sDoc, overrideData));
}

function libraryExportPath(libInstance) {
    return `./${BUtils.strToFileName(libInstance.name)}`;
}

function metadataFilePath(sDoc) {
    return `./${BUtils.strToFileName(sDoc.documentName())}.metadata`;
}

function exportAssets(assetData, archiveObj, sDoc) {
    let defaultOptions = sDoc.scaleFormat();
    BUtils.updateExportProgress(Constants.PSAssets.start);
    BUtils.logExportInfo("Exporting Assets");
    let exportPaths = new Map();
    let done = 0;
    let assetShare = (Constants.PSAssets.end - Constants.PSAssets.start) / assetData.size;
    for (const [layer, layerData] of assetData) {
        let sLayer = new SLayer(layer);
        let options = sLayer.exportOptions();
        if (!options) {
            options = defaultOptions;
        }

        let id = sLayer.layerId();
        let files = exportLayerAsset(layer, layerData.exclude, options, id, archiveObj, sDoc.uiScaleFactor());
        if (files.length > 0) {
            let pathData = {};
            pathData.path = files.shift();
            if (files.length > 0) {
                pathData.additionalAssets = files;
            }
            exportPaths.set(id, pathData);
        }

        done += assetShare;
        BUtils.updateExportProgress(Constants.PSAssets.start + done);
    }
    BUtils.updateExportProgress(Constants.PSAssets.end);
    return exportPaths;
}

function exportLayerAsset(layer, hiddenChildren, exportOptions, id, archiveObj, sf) {
    let files = [];
    let restoreVisibilty = [];
    hiddenChildren.forEach(layerToHide => {
        if (layerToHide.hidden === false) {
            restoreVisibilty.push(layerToHide);
            layerToHide.hidden = true;
        }
    });

    //To keep the png with smallest scale as the first file in the returned array.
    exportOptions.sort((l, r) => {
        if (l.formats == r.formats) {
            return l.scales > r.scales;
        } else if (l.formats == "png") {
            return false;
        }
        return true;
    });

    exportOptions.forEach(o => {
        if (Object.values(VEF).indexOf(o.formats) !== -1) {

            // Change to output to falsey, so that Sketch export returns the data and
            // not write the file. The export writes the files using the scale value and
            // this can be some odd numbers because of the golbal scaling.
            o.output = false;


            // Global scaling
            // Create a copy as we don't want to change the original object.
            let copy = { ...o };
            if (Math.abs(sf - 1.0) > Number.EPSILON)
                copy.scales *= sf;

            const buffer = sketchDom.export(layer, copy);
            let suffix = o.scales == 1 ? "" : `@${o.scales}x`;
            let fileName = `${id}${suffix}.${o.formats}`;
            archiveObj.file(fileName, buffer);
            files.push(fileName);
        } else {
            BUtils.logExportError(`Export format '${o.formats}' not supported`);
        }
    });

    // Restore visibility
    restoreVisibilty.forEach(layerToRestore => {
        layerToRestore.hidden = false;
    });
    return files;
}

function textToQml(override, sDoc) {
    return [
        {
            name: "text",
            value: `"${override.value}"`,
            targetId: override.path
        }
    ];
}

function symbolIdToUuid(override, sDoc, sLayer) {
    return [
        {
            name: "source",
            value: override.value,
            targetId: override.path
        }
    ]
}

function styleToQml(override, sDoc) {
    let styleId = override.value;
    let styleInstance = SStyle.sharedTextStyle(styleId, sDoc);
    if (!styleInstance) {
        BUtils.logExportError(`Can not find shared text style for ${styleId}`);
        return null;
    }
    let s = new SStyle(styleInstance);
    let properties = s.toQmlProperties();
    let result = [];
    for (const key in properties) {
        result.push({
            name: key,
            value: properties[key],
            targetId: override.path
        });
    }
    return result;
}

function overrideToQmlProperties(override, sDoc) {
    let type = override.property.toLowerCase();
    if (OverrideQmlMapping.has(type)) {
        return OverrideQmlMapping.get(type).call(null, override, sDoc);
    }
    return null;
}

function composeSymbolMasterReference(libraryInstance, documentInstance) {
    let mapping = new Map();
    var symbolReferences = libraryInstance.getImportableSymbolReferencesForDocument(documentInstance.instance());
    for (let ref of symbolReferences) {
        let nativeRef = ref.sketchObject;
        mapping.set(nativeRef.descriptor().sharedObjectID() + "", nativeRef.sharedObjectID() + "");
    }
    return mapping;
}

function findLinkedLibraries(sDoc, libraryMap, dependencyMap) {
    let libraries = sDoc.symbolLibraries();
    let documentPath = sDoc.instance().path;
    if (!dependencyMap.has(documentPath)) {
        dependencyMap.set(documentPath, []);
    }
    for (let libInstance of libraries) {
        let libDocInstance = libInstance.getDocument();
        let libPath = libDocInstance.path;
        dependencyMap.get(documentPath).push(libInstance);
        if (!libraryMap.has(libPath)) {
            libraryMap.set(libPath, libInstance);
            findLinkedLibraries(new SDocument(libDocInstance), libraryMap, dependencyMap);
        }
    }
}

function compileLibPaths(sDoc, libraryDependencies) {
    const documentPath = sDoc.instance().path;
    if (!libraryDependencies.has(documentPath)) {
        return [];
    }

    let relativePaths = [];
    for (let libInstance of libraryDependencies.get(documentPath)) {
        let libSDoc = new SDocument(libInstance.getDocument());
        relativePaths.push(Path.join(libraryExportPath(libInstance), metadataFilePath(libSDoc)));
    }
    return relativePaths;
}

// Remote Symbol masters are converted to local Symbol masters -> Symbols instances are
// redirected to local symbol masters
function unlinkLibraryInstances() {
    BUtils.logExportInfo(`Unlinking remote symbols`)
    let documentPath = this._sDoc.instance().path;
    this._sDoc.forEachDocumentLayer(sLayer => {
        let libInstance = sLayer.libraryInstance();
        if (libInstance) {
            // Layer is a symbol instance linked to user library.
            try {
                let libPath = libInstance.getDocument().path;
                if (libPath != documentPath) {
                    // The symbol master is in remote lib. Detach symbol recursively
                    sLayer.instance().detach({ recursively: true });
                }
            } catch (e) {
                BUtils.logExportError(`Can not detach symbol ${sLayer.layerName()}. ${e}`)
            }
        }
        return true;
    });
}

function relinkLibraryInstances() {
    // Undo all the remote symbols detach.
    // Symbol intances links are re-established to remote libraries
    BUtils.logExportInfo('Re-linking library symbols');
    if (!BUtils.doAction(Constants.Actions.Undo)) {
        BUtils.showMessage(`Re-linking library symbols failed.`,
            `Manually do "Undo (CMD + Z)" action to re-link library Symbols`);
    }
}

function applyUiScale(data, scaleFactor) {
    for (const prop in data) {
        if (Array.isArray(data[prop])) {
            for (let o of data[prop]) {
                if (o && typeof o == "object")
                    applyUiScale(o, scaleFactor)
            }
        } else if (data[prop] && typeof data[prop] == "object") {
            applyUiScale(data[prop], scaleFactor)
        } else {
            if (ScaleProps.has(prop)) {
                if (data[prop])
                    data[prop] *= scaleFactor
            }
        }
    }
}

